import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Test console"` — P11-09.
 *
 * AMENDED 2026-10-09 (design sync, `WeTalk.dc.html` "Test console"): the page is the agent picker and
 * ONE test card — the same panel the agent's page docks at its side (`TestChatPanel`, `layout="card"`),
 * with its three modes: Text, Browser voice, and the phone (**My phone** for an agent that answers
 * calls, **Call me** for one that makes them). The voice test form and the side panel beside it are
 * gone from this page, and so is the "Sandbox · runs against the live agent" kicker. Each agent in the
 * picker says which kind it is ("· answers calls", "· makes calls").
 *
 * Kept from customer-portal review #55, #167, #169: only agents that can answer are offered (an agent
 * with no ready live version is listed under "Not ready", disabled, with why); two agents with the same
 * name are told apart by their live version and number; with none ready the page says so; and the agent
 * is in the URL (`/test-console?agent=<id>`), so a link from an agent's page opens on that agent.
 */
import { useQuery } from "@tanstack/react-query";
import { Field, Icon, Select } from "@wetalk/ui";
import type { ReactNode } from "react";

import { TestChatPanel } from "./test-chat-panel.js";
import { isSurveyAgentTemplate } from "../agents/[id]/agent-tabs.js";
import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { voiceAgentListQuery } from "../../api/query.js";
import type { VoiceAgentView } from "../../api/gateway.js";
import { displayNumberOf } from "../numbers/number-display.js";

import "./test-console.css";

/**
 * Whether an agent can take a test: it has a live version. The API's own check is the same (the live
 * version must exist and be ready), so a rebuild that failed after a publish still leaves the live
 * version testable.
 */
export function isTestable(one: VoiceAgentView): boolean {
  return one.liveAgentVersionId !== null;
}

/** Why an agent cannot take a test, in the owner's words. */
function notReadyReason(one: VoiceAgentView): string {
  if (one.state === "failed") return portalText("operations", "build_failed_44de7ee9");
  if (one.state === "building") return portalText("operations", "still_building_d6dbfa31");
  return portalText("operations", "nothing_published_7f1f670a");
}

/**
 * The picker's label: the name, and — when two agents share it — what tells them apart (review
 * #167): the live version and the number.
 */
export function pickerLabelOf(one: VoiceAgentView, all: readonly VoiceAgentView[]): string {
  const shared = all.filter((other) => other.displayName === one.displayName).length > 1;
  if (!shared) return one.displayName;
  const detail = [
    one.liveAgentVersionId === null ? null : one.liveAgentVersionLabel,
    one.partyNumber === null ? portalText("operations", "no_number_16a20ef7") : displayNumberOf(one.partyNumber),
  ].filter((part): part is string => part !== null);
  return `${one.displayName} · ${detail.join(" · ")}`;
}

/** What the agent does on the phone, as the picker says it. */
export function phoneRoleOf(one: VoiceAgentView): string {
  return isSurveyAgentTemplate(one.agentTemplateCode) ? portalText("operations", "makes_calls_7fb2ea7e") : portalText("operations", "answers_calls_68ebb721");
}

export interface SandboxConsoleScreenProps {
  /** `?agent=` — the agent to open on. Empty: the first agent that can take a test. */
  readonly voiceAgentId: string;
  readonly onVoiceAgent: (voiceAgentId: string) => void;
}

export function SandboxConsoleScreen({ voiceAgentId, onVoiceAgent }: SandboxConsoleScreenProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const voiceAgentList = useQuery(voiceAgentListQuery(gateway));

  const all = voiceAgentList.data ?? [];
  const ready = all.filter(isTestable);
  const notReady = all.filter((one) => !isTestable(one));
  const asked = all.find((one) => one.voiceAgentId === voiceAgentId);
  const chosen = asked !== undefined && isTestable(asked) ? asked : ready[0];

  return (
    <section className="c-page tc-page">
      <h1 className="tc-title">{portalText("operations", "test_console_6c17ed8f")}</h1>

      {voiceAgentList.data === undefined ? (
        <p className="c-note">{portalText("operations", "loading_your_agents_3cbb7a6e")}</p>
      ) : ready.length === 0 ? (
        <div className="tc-unavailable" role="status">
          <span className="tc-unavailable-title">{portalText("operations", "no_agent_is_ready_to_test_yet_finish_a_build_first_bb7803f6")}</span>
          <p className="tc-unavailable-dek">
            {portalText("operations", "an_agent_can_be_tested_once_its_build_has_finished_and_a_v_18c1ac67")}{notReady.length === 0 ? null : portalText("operations", "not_ready_value_bf6984b1", { v0: String(notReady.map((one) => `${one.displayName} (${notReadyReason(one)})`).join(", ")) })}
          </p>
        </div>
      ) : (
        <>
          <div className="tc-picker">
            <Field label={portalText("operations", "agent_5ce2e6f4")}>
              {(control) => (
                <span className="tc-select">
                  <Select
                    {...control}
                    value={chosen?.voiceAgentId ?? ""}
                    onChange={(event) => {
                      onVoiceAgent(event.currentTarget.value);
                    }}
                  >
                    {ready.map((one) => (
                      <option key={one.voiceAgentId} value={one.voiceAgentId}>
                        {`${pickerLabelOf(one, all)} · ${phoneRoleOf(one)}`}
                      </option>
                    ))}
                    {notReady.length === 0 ? null : (
                      <optgroup label={portalText("operations", "not_ready_2b50ff80")}>
                        {notReady.map((one) => (
                          <option key={one.voiceAgentId} value={one.voiceAgentId} disabled>
                            {`${pickerLabelOf(one, all)} — ${notReadyReason(one)}`}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </Select>
                  <Icon glyph={GLYPH.caretDown} size="sm" />
                </span>
              )}
            </Field>
            {asked !== undefined && !isTestable(asked) ? (
              <p className="tc-picker-note" role="status">
                {asked.displayName} {portalText("operations", "cannot_be_tested_42755255")}{notReadyReason(asked)}{portalText("operations", "so_the_console_opened_on_2f7f701a")}{" "}
                {chosen?.displayName ?? portalText("operations", "another_agent_f81c3814")}.
              </p>
            ) : null}
          </div>
          {chosen === undefined ? null : (
            <TestChatPanel
              key={chosen.voiceAgentId}
              layout="card"
              voiceModes
              voiceAgent={chosen}
              voiceAgentId={chosen.voiceAgentId}
              voiceAgentName={chosen.displayName}
              languageCode={chosen.languageCode}
              onOpenTestCall={null}
            />
          )}
        </>
      )}
    </section>
  );
}
